import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment-timezone';
import useActivation from '@/src/Activations/components/hooks/useActivation';
import useShare from '@/src/Share/components/hooks/useShare';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import BulkUploadMedia from '@/src/Common/components/BulkUploadMedia';
import { useEventContext } from '@/src/v2/components/EventContext';
import EventPage from '../../chrome/EventPage';
import { ConfirmDialog, Crumb, Field, SectionCard, TextInput } from '../../components/forms';
import { PageHeadActions } from '../../components/nav';
import { useToast } from '../../components/roster';
import { TCard, TEmpty, TSkeleton } from '../../components/table';
import '../../pages-event.css';
import './insights.css';

/*
 * Session — one guest's session: its photos (hide/show), its shares
 * (add = queue only; Send delivers), upload into it, copy the guest
 * link. A new session is a fresh client-side UUID: it exists once
 * photos or shares reference it.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function destinationError(value: string): string | undefined {
    const v = value.trim();
    if (!v) return undefined;
    /* the hook types anything with "@" as email, everything else as SMS */
    if (v.includes('@')) return EMAIL_RE.test(v) ? undefined : 'Enter a valid email address.';
    const digits = v.replace(/\D/g, '');
    if (!/^\+?[\d\s().-]+$/.test(v) || digits.length < 7) return 'Enter an email address or a phone number.';
    return undefined;
}

const plural = (n: number, word: string, many = `${word}s`) => `${n.toLocaleString()} ${n === 1 ? word : many}`;

function SessionBody() {
    const navigate = useNavigate();
    const { photoSessionId } = useParams();
    const { activationId: id, galleryId, resolved, invalidateMedia } = useEventContext();
    const { getSessionPhotos } = useActivation();
    const { getSharesForActivation, sendUnsentSharesForSession, addShareToSession } = useShare();
    const authenticatedApi = useAuthenticatedApi();
    const toast = useToast();

    const [isNew] = useState(() => !photoSessionId || photoSessionId === 'new');
    const [sessionId] = useState<string>(() => (!photoSessionId || photoSessionId === 'new' ? crypto.randomUUID() : photoSessionId));

    const [photos, setPhotos] = useState<any[] | null>(null);
    const [photosFailed, setPhotosFailed] = useState(false);
    const [shares, setShares] = useState<any[] | null>(null);
    const [sharesFailed, setSharesFailed] = useState(false);
    const [showHidden, setShowHidden] = useState(false);
    const [toggling, setToggling] = useState<Set<number>>(() => new Set());

    const [newDest, setNewDest] = useState('');
    const [touched, setTouched] = useState(false);
    const [adding, setAdding] = useState(false);
    const [addError, setAddError] = useState<string | null>(null);

    const [confirmSend, setConfirmSend] = useState(false);
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);

    const [uploadOpen, setUploadOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const busy = useRef(false);
    const mounted = useRef(true);
    useEffect(
        () => () => {
            mounted.current = false;
        },
        [],
    );

    const loadPhotos = useCallback(async () => {
        setPhotosFailed(false);
        try {
            const rows = await getSessionPhotos(id, sessionId);
            if (mounted.current) setPhotos(Array.isArray(rows) ? rows : []);
        } catch {
            if (mounted.current) setPhotosFailed(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, sessionId]);

    const loadShares = useCallback(async () => {
        setSharesFailed(false);
        try {
            const all = await getSharesForActivation(id);
            /* one row per (share, media): keep one per share, this session only */
            const seen = new Set<number>();
            const mine = (Array.isArray(all) ? all : []).filter((s: any) => {
                if (s.photoSessionId !== sessionId || s.id == null || seen.has(s.id)) return false;
                seen.add(s.id);
                return true;
            });
            if (mounted.current) setShares(mine);
        } catch {
            if (mounted.current) setSharesFailed(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, sessionId]);

    useEffect(() => {
        if (!id) return;
        loadPhotos();
        loadShares();
    }, [id, loadPhotos, loadShares]);

    /* once a new session has content, give it a real URL so a reload keeps it */
    const adopt = () => {
        if (isNew) navigate(`/activations/${id}/insights/session/${sessionId}`, { replace: true });
    };

    const all = useMemo(() => photos ?? [], [photos]);
    const visible = useMemo(() => all.filter((p) => showHidden || !p.isHidden), [all, showHidden]);
    const hiddenCount = all.filter((p) => p.isHidden).length;
    const unsentShares = useMemo(() => (shares ?? []).filter((s) => !s.sent), [shares]);
    const sessionTime = all[0]?.capturedAt ?? all[0]?.createdAt;

    const toggleHidden = async (m: any) => {
        if (toggling.has(m.id)) return;
        const next = !m.isHidden;
        setToggling((t) => new Set(t).add(m.id));
        setPhotos((rows) => (rows ?? []).map((r) => (r.id === m.id ? { ...r, isHidden: next } : r)));
        const ok = await authenticatedApi
            .put(`media/${m.id}/visibility`, { isHidden: next })
            .then(() => true)
            .catch(() => false);
        if (!mounted.current) return;
        setToggling((t) => {
            const n = new Set(t);
            n.delete(m.id);
            return n;
        });
        if (ok) invalidateMedia();
        else {
            setPhotos((rows) => (rows ?? []).map((r) => (r.id === m.id ? { ...r, isHidden: m.isHidden } : r)));
            toast.show(next ? 'Couldn’t hide the photo — try again.' : 'Couldn’t show the photo — try again.');
        }
    };

    const destError = destinationError(newDest);
    const noGallery = resolved && galleryId == null;

    const addShare = async () => {
        setTouched(true);
        const dest = newDest.trim();
        if (!dest || destError || busy.current || galleryId == null) return;
        busy.current = true;
        setAdding(true);
        setAddError(null);
        /* the hook swallows errors and returns { id: null } on failure */
        const result = await addShareToSession(sessionId, all[0]?.shareIdentifier ?? sessionId, dest, id, galleryId).catch(
            () => ({ id: null }),
        );
        if (!mounted.current) return;
        if (result?.id != null) {
            setNewDest('');
            setTouched(false);
            toast.show(`Queued a share to ${dest}. It isn’t sent until you press Send.`);
            adopt();
            await loadShares();
        } else {
            setAddError(`Couldn’t add ${dest} — check it and try again.`);
        }
        busy.current = false;
        setAdding(false);
    };

    const send = async () => {
        if (busy.current || unsentShares.length === 0) return;
        busy.current = true;
        const count = unsentShares.length;
        setSending(true);
        setSendError(null);
        try {
            await sendUnsentSharesForSession(sessionId);
            if (!mounted.current) return;
            toast.show(`Sent ${plural(count, 'share')}.`);
            await loadShares();
        } catch {
            if (mounted.current) setSendError('Couldn’t send — the shares are still unsent. Try again.');
        }
        busy.current = false;
        if (mounted.current) setSending(false);
    };

    const copyLink = async () => {
        if (galleryId == null) return;
        try {
            await navigator.clipboard.writeText(`${window.location.origin}/session/${galleryId}/${sessionId}`);
            setCopied(true);
            window.setTimeout(() => mounted.current && setCopied(false), 1800);
        } catch {
            toast.show('Couldn’t copy — your browser blocked the clipboard.');
        }
    };

    const sendSummary = () => {
        const emails = unsentShares.filter((s) => s.type !== 'text').length;
        const texts = unsentShares.length - emails;
        const parts = [emails ? `email ${plural(emails, 'address', 'addresses')}` : '', texts ? `text ${plural(texts, 'phone number')}` : '']
            .filter(Boolean)
            .join(' and ');
        const names = unsentShares.map((s) => s.destination).filter(Boolean);
        const shown = names.slice(0, 5).join(', ') + (names.length > 5 ? ` and ${names.length - 5} more` : '');
        return `This will ${parts}${shown ? ` (${shown})` : ''} with a link to this session’s photos. Messages go out right away and can’t be recalled.`;
    };

    const title = isNew && all.length === 0 && (shares ?? []).length === 0 ? 'New session' : 'Session';
    const subParts = [
        photos === null ? (photosFailed ? null : 'Loading…') : plural(visible.length, 'photo'),
        sessionTime ? moment(sessionTime).format('MMM D, h:mm A') : null,
    ].filter(Boolean);

    return (
        <div className="ev-page">
            <Crumb label="Insights" onClick={() => navigate(`/activations/${id}/insights`)} />
            <PageHeadActions
                title={title}
                sub={
                    <>
                        {subParts.join(' · ')}
                        {subParts.length > 0 && ' · '}
                        <span className="ev-mono">{sessionId}</span>
                    </>
                }
                actions={
                    <>
                        <button type="button" className="tb-btn" disabled={galleryId == null} onClick={copyLink}>
                            {copied ? 'Copied' : 'Copy guest link'}
                        </button>
                        <button type="button" className="tb-btn" disabled={!resolved} onClick={() => setUploadOpen(true)}>
                            Upload
                        </button>
                        <button
                            type="button"
                            className="tb-new"
                            disabled={sending || adding || unsentShares.length === 0}
                            onClick={() => setConfirmSend(true)}
                            title={unsentShares.length === 0 ? 'No unsent shares' : undefined}
                        >
                            {sending ? 'Sending…' : unsentShares.length > 0 ? `Send ${unsentShares.length} unsent` : 'Send'}
                        </button>
                    </>
                }
            />
            {noGallery && <p className="field-e">This event has no gallery yet, so you can’t add shares or copy a guest link.</p>}
            {sendError && <p className="field-e">{sendError}</p>}

            <div className="stack">
                <SectionCard
                    title="Photos"
                    note={hiddenCount > 0 ? `${hiddenCount} hidden from the gallery` : undefined}
                    action={
                        hiddenCount > 0 ? (
                            <button type="button" className="tb-btn ghost sm" aria-pressed={showHidden} onClick={() => setShowHidden((h) => !h)}>
                                {showHidden ? 'Hide hidden' : `Show ${hiddenCount} hidden`}
                            </button>
                        ) : undefined
                    }
                >
                    {photosFailed ? (
                        <TEmpty message="Couldn’t load this session’s photos." actionLabel="Retry" onAction={loadPhotos} />
                    ) : photos === null ? (
                        <TSkeleton rows={2} />
                    ) : visible.length === 0 ? (
                        <p className="quiet-empty">
                            {all.length > 0
                                ? 'All photos in this session are hidden.'
                                : 'No photos yet — upload some, or they arrive as the guest captures.'}
                        </p>
                    ) : (
                        <div className="ins-photos">
                            {visible.map((m) => (
                                <div key={m.id} className={m.isHidden ? 'ins-ph hidden' : 'ins-ph'}>
                                    <img src={m.thumbnailUri ?? m.thumbnailTinyUri ?? m.originalUri} alt="" loading="lazy" />
                                    {m.isHidden && <span className="tag">Hidden</span>}
                                    <span className="acts">
                                        <button
                                            type="button"
                                            className="tb-btn sm"
                                            disabled={toggling.has(m.id)}
                                            aria-label={m.isHidden ? 'Show this photo in the gallery' : 'Hide this photo from the gallery'}
                                            onClick={() => toggleHidden(m)}
                                        >
                                            {m.isHidden ? 'Show' : 'Hide'}
                                        </button>
                                        {m.originalUri && (
                                            <a
                                                className="tb-btn sm"
                                                href={m.originalUri}
                                                target="_blank"
                                                rel="noreferrer"
                                                aria-label="Open the original in a new tab"
                                            >
                                                Open
                                            </a>
                                        )}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </SectionCard>

                <SectionCard
                    title="Shares"
                    note={shares && shares.length > 0 ? `${shares.length - unsentShares.length} sent · ${unsentShares.length} unsent` : undefined}
                >
                    {sharesFailed ? (
                        <TEmpty message="Couldn’t load this session’s shares." actionLabel="Retry" onAction={loadShares} />
                    ) : shares === null ? (
                        <TSkeleton rows={2} />
                    ) : shares.length === 0 ? (
                        <p className="quiet-empty">No shares on this session yet.</p>
                    ) : (
                        <TCard>
                            {shares.map((s: any) => (
                                <div className="trow ev-row" key={s.id}>
                                    <span className="main">
                                        <span className="nm">{s.destination || '—'}</span>
                                        <span className="sub">{s.type === 'text' ? 'SMS' : `${s.type ?? ''}`.replace(/^\w/, (c) => c.toUpperCase())}</span>
                                    </span>
                                    <span className={s.sent ? 'ev-st ok' : 'ev-st'}>{s.sent ? 'Sent' : 'Unsent'}</span>
                                </div>
                            ))}
                        </TCard>
                    )}

                    <form
                        className="ins-add"
                        noValidate
                        onSubmit={(e) => {
                            e.preventDefault();
                            addShare();
                        }}
                    >
                        <Field label="Add destination" error={(touched && destError) || addError || undefined}>
                            <TextInput
                                type="text"
                                inputMode="email"
                                placeholder="guest@example.com or +1 555 123 4567"
                                value={newDest}
                                disabled={adding || noGallery}
                                aria-invalid={(touched && destError) || addError ? true : undefined}
                                onBlur={() => setTouched(true)}
                                onChange={(e) => {
                                    setNewDest(e.target.value);
                                    setAddError(null);
                                }}
                            />
                        </Field>
                        <button type="submit" className="tb-btn" disabled={adding || !newDest.trim() || galleryId == null}>
                            {adding ? 'Adding…' : 'Add share'}
                        </button>
                    </form>
                    <p className="ins-note">Added shares are queued, not sent — press Send to message them.</p>
                </SectionCard>
            </div>

            {uploadOpen && (
                /* BulkUploadMedia brings its own modal; the wrapper only bridges tokens */
                <div className="psv3-legacy">
                    <BulkUploadMedia
                        activationId={id}
                        galleryId={galleryId}
                        photoSessionId={sessionId}
                        onCancel={() => setUploadOpen(false)}
                        onComplete={() => {
                            setUploadOpen(false);
                            invalidateMedia();
                            adopt();
                            loadPhotos();
                        }}
                    />
                </div>
            )}

            <ConfirmDialog
                open={confirmSend}
                title={`Send ${plural(unsentShares.length, 'share')}?`}
                body={sendSummary()}
                actionLabel={`Send to ${unsentShares.length}`}
                danger={false}
                onConfirm={send}
                onClose={() => setConfirmSend(false)}
            />
            {toast.node}
        </div>
    );
}

export default function SessionV3() {
    return (
        <EventPage section="insights">
            <SessionBody />
        </EventPage>
    );
}
